/**
 * 搜索页面
 * 对标search-prototype.html的完整功能实现
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Layout, Input, Button, Radio, Card, List, Tag, Empty,
  Spin, Space, Select, DatePicker, Drawer, Badge, Typography,
  Row, Col, Divider, message
} from 'antd'
import {
  SearchOutlined, FilterOutlined, AppstoreOutlined, UnorderedListOutlined,
  ClockCircleOutlined, TagOutlined, FolderOutlined, CalendarOutlined,
  FileTextOutlined, EyeOutlined, StarOutlined, HomeOutlined
} from '@ant-design/icons'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { api } from '../services/api'
import dayjs, { Dayjs } from 'dayjs'
import './Search.css'

const { Header, Content, Sider } = Layout
const { Search: AntSearch } = Input
const { RangePicker } = DatePicker
const { Title, Text, Paragraph } = Typography

// Markdown渲染组件配置
const MarkdownComponents = {
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '')
    return !inline && match ? (
      <SyntaxHighlighter
        style={tomorrow}
        language={match[1]}
        PreTag="div"
        {...props}
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    )
  },
  img({ src, alt, ...props }: any) {
    return (
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '16px auto' }}
        {...props}
      />
    )
  }
}

interface Article {
  id: number
  title: string
  url: string
  content: string
  summary: string
  markdown_content: string
  site_name: string
  author: string | null
  published_at: string | null
  collected_at: string
  reading_time: number
  word_count: number
  tags: Array<{ id: number; name: string; color: string }>
  folders: Array<{ id: number; name: string }>
}

interface SearchFilters {
  search_type: 'keyword' | 'semantic'
  tags: string[]
  folder_id: number | null
  date_range: [Dayjs, Dayjs] | null
  sort_by: string
  order: 'asc' | 'desc'
}

const Search: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // 状态管理
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list')
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false)
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
  const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false)
  
  // 筛选条件
  const [filters, setFilters] = useState<SearchFilters>({
    search_type: 'keyword',
    tags: [],
    folder_id: null,
    date_range: null,
    sort_by: 'collected_at',
    order: 'desc'
  })
  
  // 可用的标签和文件夹
  const [availableTags, setAvailableTags] = useState<Array<{ id: number; name: string; color: string }>>([])
  const [availableFolders, setAvailableFolders] = useState<Array<{ id: number; name: string }>>([])

  // 推荐功能
  const [recommendations, setRecommendations] = useState<Article[]>([])
  const [showRecommendations, setShowRecommendations] = useState(false)
  const [recommendationLoading, setRecommendationLoading] = useState(false)

  useEffect(() => {
    loadTagsAndFolders()
    loadRecommendations()

    // 从URL参数或sessionStorage恢复筛选条件（详情页返回时使用sessionStorage）
    let saved: any = {}
    try {
      const raw = sessionStorage.getItem('search_list_state')
      saved = raw ? JSON.parse(raw) : {}
    } catch {}

    const q = searchParams.get('q') ?? saved.query ?? ''
    const tag = searchParams.get('tag')
    const folder = searchParams.get('folder')
    const folderId = searchParams.get('folder_id')
    const page = parseInt(searchParams.get('page') || '') || saved.page || 1
    const searchType = (searchParams.get('type') as any) || saved.search_type || 'keyword'
    const tagsParam = searchParams.get('tags')
    const tags = tagsParam ? tagsParam.split(',') : (saved.tags || (tag ? [tag] : []))
    const folderIdNum = folderId ? parseInt(folderId) : (saved.folder_id ?? null)
    const sortBy = searchParams.get('sort') || saved.sort_by || 'collected_at'
    const order = (searchParams.get('order') as any) || saved.order || 'desc'

    setQuery(q)
    setPage(page)
    setFilters({
      search_type: searchType,
      tags,
      folder_id: folderIdNum,
      date_range: null,
      sort_by: sortBy,
      order
    })

    if (q || tags.length > 0 || folderIdNum) {
      handleSearch(q)
    }

    if (folder && !folderIdNum) {
      // 根据文件夹名称查找ID（需要等待文件夹数据加载完成）
      setTimeout(() => {
        const foundFolder = availableFolders.find(f => f.name === folder)
        if (foundFolder) {
          setFilters(prev => ({ ...prev, folder_id: foundFolder.id }))
        }
      }, 100)
    }
  }, [])

  useEffect(() => {
    const q = searchParams.get('q')
    if (q && q !== query) {
      setQuery(q)
      handleSearch(q)
    }
  }, [searchParams])

  // 监听搜索类型变化，自动重新搜索
  useEffect(() => {
    if (query.trim() && articles.length > 0) {
      handleSearch()
    }
  }, [filters.search_type])

  const loadTagsAndFolders = async () => {
    try {
      const [tagsRes, foldersRes] = await Promise.all([
        api.tags.list({ page_size: 100 }),
        api.folders.list({ page_size: 100 })
      ])
      
      if (tagsRes.success) {
        setAvailableTags(tagsRes.data || [])
      }

      if (foldersRes.success) {
        setAvailableFolders(foldersRes.data || [])
      }
    } catch (error) {
      console.error('加载标签和文件夹失败:', error)
    }
  }

  const loadRecommendations = async () => {
    try {
      setRecommendationLoading(true)
      const response = await api.recommendations.articles({ limit: 10 })

      if (response.success || response.data) {
        const data = response.data || response
        setRecommendations(data.items || [])
      }
    } catch (error) {
      console.error('加载推荐失败:', error)
    } finally {
      setRecommendationLoading(false)
    }
  }

  const handleSearch = async (searchQuery?: string) => {
    const q = searchQuery || query
    if (!q.trim()) {
      message.warning('请输入搜索关键词')
      return
    }

    try {
      setLoading(true)

      let response: any

      // 根据搜索类型选择不同的API
      if (filters.search_type === 'semantic' || filters.search_type === 'hybrid') {
        // 语义搜索或混合搜索
        const params: any = {
          q,
          search_type: filters.search_type,
          page,
          page_size: pageSize,
          sort_by: filters.sort_by,
          order: filters.order
        }

        // 添加标签筛选（支持多个标签，OR关系）
        if (filters.tags.length > 0) {
          params.tags = filters.tags.join(',')
        }

        // 添加文件夹筛选
        if (filters.folder_id) {
          params.folder_id = filters.folder_id
        }

        response = await api.search.articles(params)

      } else {
        // 关键词搜索（原有逻辑）
        const params: any = {
          page,
          page_size: pageSize,
          search: q,
          sort_by: filters.sort_by,
          order: filters.order
        }

        // 添加标签筛选（支持多个标签，OR关系）
        if (filters.tags.length > 0) {
          params.tags = filters.tags.join(',')
        }

        // 添加文件夹筛选
        if (filters.folder_id) {
          params.folder_id = filters.folder_id
        }

        response = await api.articles.list(params)
      }

      if (response.success || response.data) {
        const data = response.data || response
        setArticles(data.items || [])
        setTotal(data.total || 0)
      }

    } catch (error: any) {
      console.error('搜索失败:', error)
      message.error('搜索失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSearchSubmit = (value: string) => {
    setQuery(value)
    setSearchParams({ q: value })
    setPage(1)
    handleSearch(value)
  }

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const applyFilters = () => {
    setPage(1)
    handleSearch()
    setFilterDrawerVisible(false)
  }

  const resetFilters = () => {
    setFilters({
      search_type: 'keyword',
      tags: [],
      folder_id: null,
      date_range: null,
      sort_by: 'collected_at',
      order: 'desc'
    })
  }

  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article)
    setPreviewDrawerVisible(true)
  }

  // 保存搜索状态后跳转到详情页，以便返回时恢复
  const navigateToArticle = (id: number) => {
    sessionStorage.setItem('search_list_state', JSON.stringify({
      query,
      page,
      search_type: filters.search_type,
      tags: filters.tags,
      folder_id: filters.folder_id,
      sort_by: filters.sort_by,
      order: filters.order
    }))
    navigate(`/articles/${id}`)
  }

  // 渲染Markdown内容的组件
  const renderMarkdown = (markdown: string) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={MarkdownComponents}
      >
        {markdown}
      </ReactMarkdown>
    )
  }

  const activeFiltersCount = 
    filters.tags.length + 
    (filters.folder_id ? 1 : 0) + 
    (filters.date_range ? 1 : 0)

  return (
    <Layout className="search-layout">
      <Header className="search-header">
        <div className="header-left">
          <div className="logo" onClick={() => navigate('/')}>
            <HomeOutlined style={{ fontSize: 24, color: 'white' }} />
            <span className="logo-text">CrawlBox</span>
          </div>
        </div>
        
        <div className="header-search">
          <div className="search-input-container">
            <AntSearch
              placeholder="搜索你的知识库..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onSearch={handleSearchSubmit}
              size="large"
              enterButton={<SearchOutlined />}
              allowClear
              addonBefore={
                <Radio.Group
                  value={filters.search_type}
                  onChange={(e) => {
                    handleFilterChange('search_type', e.target.value)
                  }}
                  size="small"
                  style={{ border: 'none' }}
                >
                  <Radio.Button value="keyword">关键词</Radio.Button>
                  <Radio.Button value="semantic">语义</Radio.Button>
                  <Radio.Button value="hybrid">混合</Radio.Button>
                </Radio.Group>
              }
            />
          </div>
        </div>
        
        <div className="header-right">
          <Space>
            <Badge count={activeFiltersCount} offset={[-5, 5]}>
              <Button 
                icon={<FilterOutlined />}
                onClick={() => setFilterDrawerVisible(true)}
              >
                筛选
              </Button>
            </Badge>
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="list">
                <UnorderedListOutlined /> 列表
              </Radio.Button>
              <Radio.Button value="card">
                <AppstoreOutlined /> 卡片
              </Radio.Button>
            </Radio.Group>
          </Space>
        </div>
      </Header>

      <Content className="search-content">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" tip="搜索中..." />
          </div>
        ) : articles.length === 0 && !showRecommendations ? (
          <div>
            <Empty
              description={query ? `没有找到与"${query}"相关的内容` : '开始搜索你的知识库'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button
                type="primary"
                onClick={() => setShowRecommendations(true)}
                loading={recommendationLoading}
              >
                查看推荐内容
              </Button>
            </div>
          </div>
        ) : showRecommendations && articles.length === 0 ? (
          <div>
            <div className="recommendations-header">
              <Title level={4}>为你推荐</Title>
              <Button
                type="link"
                onClick={() => setShowRecommendations(false)}
              >
                返回搜索
              </Button>
            </div>
            {recommendationLoading ? (
              <div className="loading-container">
                <Spin size="large" tip="加载推荐中..." />
              </div>
            ) : recommendations.length === 0 ? (
              <Empty description="暂无推荐内容" />
            ) : (
              <div className="recommendations-content">
                {viewMode === 'list' ? (
                  <List
                    dataSource={recommendations}
                    renderItem={(article) => (
                      <List.Item
                        key={article.id}
                        actions={[
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedArticle(article)
                              setPreviewDrawerVisible(true)
                            }}
                          >
                            预览
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault()
                                setSelectedArticle(article)
                                setPreviewDrawerVisible(true)
                              }}
                            >
                              {article.title}
                            </a>
                          }
                          description={
                            <div>
                              <Text type="secondary">{article.summary}</Text>
                              <div style={{ marginTop: 8 }}>
                                <Tag color="blue">推荐</Tag>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {dayjs(article.collected_at).format('YYYY-MM-DD')}
                                </Text>
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <Row gutter={[16, 16]}>
                    {recommendations.map((article) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={article.id}>
                        <Card
                          hoverable
                          size="small"
                          title={
                            <div style={{ fontSize: 14, lineHeight: '20px' }}>
                              {article.title}
                            </div>
                          }
                          extra={
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => {
                                setSelectedArticle(article)
                                setPreviewDrawerVisible(true)
                              }}
                            />
                          }
                          onClick={() => {
                            setSelectedArticle(article)
                            setPreviewDrawerVisible(true)
                          }}
                        >
                          <Paragraph
                            ellipsis={{ rows: 3 }}
                            style={{ fontSize: 12, margin: 0 }}
                          >
                            {article.summary}
                          </Paragraph>
                          <div style={{ marginTop: 8 }}>
                            <Tag color="blue" size="small">推荐</Tag>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {dayjs(article.collected_at).format('MM-DD')}
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="search-results-header">
              <div>
                <Text type="secondary">
                  找到 <Text strong>{total}</Text> 个结果
                </Text>
                {filters.search_type !== 'keyword' && (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    {filters.search_type === 'semantic' ? '语义搜索' : '混合搜索'}
                  </Tag>
                )}
              </div>
              <Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setShowRecommendations(!showRecommendations)}
                >
                  {showRecommendations ? '隐藏推荐' : '显示推荐'}
                </Button>
                <Select
                  value={`${filters.sort_by}-${filters.order}`}
                  onChange={(value) => {
                    const [sort_by, order] = value.split('-')
                    handleFilterChange('sort_by', sort_by)
                    handleFilterChange('order', order as 'asc' | 'desc')
                    handleSearch()
                  }}
                  style={{ width: 200 }}
                >
                  <Select.Option value="collected_at-desc">最新收藏</Select.Option>
                  <Select.Option value="collected_at-asc">最早收藏</Select.Option>
                  <Select.Option value="reading_time-desc">阅读时间长</Select.Option>
                  <Select.Option value="reading_time-asc">阅读时间短</Select.Option>
                </Select>
              </Space>
            </div>

            {viewMode === 'list' ? (
              <List
                dataSource={articles}
                renderItem={(article) => (
                  <Card 
                    className="article-list-item"
                    hoverable
                    onClick={() => handleArticleClick(article)}
                  >
                    <div className="article-header">
                      <Title level={4} className="article-title">
                        {article.title}
                      </Title>
                      <Space>
                        <Button
                          type="primary"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(article.url, '_blank')
                          }}
                          style={{
                            background: 'var(--success)',
                            border: 'none',
                            borderRadius: '16px',
                            fontWeight: 500
                          }}
                        >
                          原文
                        </Button>
                        <Button
                          type="default"
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigateToArticle(article.id)
                          }}
                          style={{
                            borderRadius: '16px'
                          }}
                        >
                          详情
                        </Button>
                      </Space>
                    </div>
                    
                    <Paragraph 
                      className="article-summary"
                      ellipsis={{ rows: 2 }}
                    >
                      {article.summary}
                    </Paragraph>
                    
                    <div className="article-meta">
                      <Space size="small" wrap>
                        <Tag icon={<ClockCircleOutlined />}>
                          {article.reading_time}分钟
                        </Tag>
                        <Tag>{article.site_name}</Tag>
                        {article.author && <Tag>@{article.author}</Tag>}
                        <Tag icon={<CalendarOutlined />}>
                          {dayjs(article.collected_at).format('YYYY-MM-DD')}
                        </Tag>
                        {/* 显示相似度分数（仅在语义搜索或混合搜索时显示） */}
                        {article.similarity !== undefined && article.similarity !== null && (
                          <Tag color="purple" style={{ fontWeight: 'bold' }}>
                            相似度 {(article.similarity * 100).toFixed(0)}%
                          </Tag>
                        )}
                      </Space>
                      
                      {article.tags && article.tags.length > 0 && (
                        <Space size="small" wrap>
                          {article.tags.map(tag => (
                            <Tag key={tag.id} color={tag.color || 'blue'}>
                              {tag.name}
                            </Tag>
                          ))}
                        </Space>
                      )}
                    </div>
                  </Card>
                )}
              />
            ) : (
              <Row gutter={[16, 16]}>
                {articles.map(article => (
                  <Col key={article.id} xs={24} sm={12} lg={8} xl={6}>
                    <Card
                      className="article-card-item"
                      hoverable
                      onClick={() => handleArticleClick(article)}
                      cover={
                        <div className="card-cover">
                          <FileTextOutlined style={{ fontSize: 48, color: 'var(--brand)' }} />
                        </div>
                      }
                      actions={[
                        <Button
                          type="primary"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(article.url, '_blank')
                          }}
                          style={{
                            background: 'var(--success)',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: 500
                          }}
                        >
                          原文
                        </Button>,
                        <Button
                          type="default"
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigateToArticle(article.id)
                          }}
                          style={{
                            borderRadius: '12px'
                          }}
                        >
                          详情
                        </Button>
                      ]}
                    >
                      <Card.Meta
                        title={article.title}
                        description={
                          <>
                            <Paragraph ellipsis={{ rows: 3 }}>
                              {article.summary}
                            </Paragraph>
                            <Space size="small" wrap>
                              <Tag icon={<ClockCircleOutlined />} color="blue">
                                {article.reading_time}分钟
                              </Tag>
                              {/* 显示相似度分数 */}
                              {article.similarity !== undefined && article.similarity !== null && (
                                <Tag color="purple" style={{ fontWeight: 'bold' }}>
                                  相似度 {(article.similarity * 100).toFixed(0)}%
                                </Tag>
                              )}
                              {article.tags.slice(0, 2).map(tag => (
                                <Tag key={tag.id} color={tag.color}>
                                  {tag.name}
                                </Tag>
                              ))}
                            </Space>
                          </>
                        }
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            )}

            {/* 推荐内容区域 */}
            {showRecommendations && recommendations.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <Divider>
                  <Title level={5} style={{ margin: 0 }}>
                    为你推荐
                  </Title>
                </Divider>
                {viewMode === 'list' ? (
                  <List
                    size="small"
                    dataSource={recommendations.slice(0, 5)}
                    renderItem={(article) => (
                      <List.Item
                        key={`rec-${article.id}`}
                        actions={[
                          <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedArticle(article)
                              setPreviewDrawerVisible(true)
                            }}
                          >
                            预览
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault()
                                setSelectedArticle(article)
                                setPreviewDrawerVisible(true)
                              }}
                              style={{ fontSize: 14 }}
                            >
                              {article.title}
                            </a>
                          }
                          description={
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {article.summary?.substring(0, 100)}...
                              </Text>
                              <div style={{ marginTop: 4 }}>
                                <Tag color="orange" size="small">推荐</Tag>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {dayjs(article.collected_at).format('MM-DD')}
                                </Text>
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <Row gutter={[12, 12]}>
                    {recommendations.slice(0, 8).map((article) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={`rec-${article.id}`}>
                        <Card
                          hoverable
                          size="small"
                          title={
                            <div style={{ fontSize: 13, lineHeight: '18px' }}>
                              {article.title}
                            </div>
                          }
                          extra={
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => {
                                setSelectedArticle(article)
                                setPreviewDrawerVisible(true)
                              }}
                            />
                          }
                          onClick={() => {
                            setSelectedArticle(article)
                            setPreviewDrawerVisible(true)
                          }}
                        >
                          <Paragraph
                            ellipsis={{ rows: 2 }}
                            style={{ fontSize: 11, margin: 0 }}
                          >
                            {article.summary}
                          </Paragraph>
                          <div style={{ marginTop: 6 }}>
                            <Tag color="orange" size="small">推荐</Tag>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                              {dayjs(article.collected_at).format('MM-DD')}
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            )}
          </>
        )}
      </Content>

      {/* 筛选抽屉 */}
      <Drawer
        title="高级筛选"
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={400}
        extra={
          <Space>
            <Button onClick={resetFilters}>重置</Button>
            <Button type="primary" onClick={applyFilters}>
              应用
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={5}>搜索类型</Title>
            <Radio.Group
              value={filters.search_type}
              onChange={(e) => handleFilterChange('search_type', e.target.value)}
              style={{ width: '100%' }}
            >
              <Radio.Button value="keyword" style={{ width: '33.33%', textAlign: 'center' }}>
                关键词
              </Radio.Button>
              <Radio.Button value="semantic" style={{ width: '33.33%', textAlign: 'center' }}>
                语义
              </Radio.Button>
              <Radio.Button value="hybrid" style={{ width: '33.33%', textAlign: 'center' }}>
                混合
              </Radio.Button>
            </Radio.Group>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
              语义搜索基于AI理解内容含义
            </Text>
          </div>

          <Divider />

          <div>
            <Title level={5}>
              <TagOutlined /> 标签筛选
            </Title>
            <Select
              mode="multiple"
              placeholder="选择标签"
              value={filters.tags}
              onChange={(value) => handleFilterChange('tags', value)}
              style={{ width: '100%' }}
              maxTagCount={3}
            >
              {availableTags.map(tag => (
                <Select.Option key={tag.id} value={tag.name}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Title level={5}>
              <FolderOutlined /> 文件夹筛选
            </Title>
            <Select
              placeholder="选择文件夹"
              value={filters.folder_id}
              onChange={(value) => handleFilterChange('folder_id', value)}
              style={{ width: '100%' }}
              allowClear
            >
              {availableFolders.map(folder => (
                <Select.Option key={folder.id} value={folder.id}>
                  {folder.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Title level={5}>
              <CalendarOutlined /> 日期范围
            </Title>
            <RangePicker
              value={filters.date_range}
              onChange={(dates) => handleFilterChange('date_range', dates)}
              style={{ width: '100%' }}
            />
          </div>
        </Space>
      </Drawer>

      {/* 文章预览抽屉 */}
      <Drawer
        title={selectedArticle?.title}
        placement="right"
        onClose={() => setPreviewDrawerVisible(false)}
        open={previewDrawerVisible}
        width={800}
        extra={
          <Space>
            <Button 
              icon={<EyeOutlined />}
              onClick={() => window.open(selectedArticle?.url, '_blank')}
            >
              查看原文
            </Button>
            <Button 
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => {
                setPreviewDrawerVisible(false)
                navigateToArticle(selectedArticle!.id)
              }}
            >
              查看详情
            </Button>
          </Space>
        }
      >
        {selectedArticle && (
          <div className="article-preview">
            <div className="preview-meta">
              <Space size="small" wrap>
                <Tag icon={<ClockCircleOutlined />} color="blue">
                  {selectedArticle.reading_time}分钟
                </Tag>
                <Tag>{selectedArticle.site_name}</Tag>
                {selectedArticle.author && <Tag>@{selectedArticle.author}</Tag>}
                <Tag icon={<CalendarOutlined />}>
                  {dayjs(selectedArticle.collected_at).format('YYYY-MM-DD HH:mm')}
                </Tag>
              </Space>
              
              {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Space size="small" wrap>
                    {selectedArticle.tags.map(tag => (
                      <Tag key={tag.id} color={tag.color || 'blue'}>
                        {tag.name}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>
            
            <Divider />
            
            <div className="markdown-content">
              {renderMarkdown(selectedArticle.markdown_content || selectedArticle.summary)}
            </div>
          </div>
        )}
      </Drawer>
    </Layout>
  )
}

export default Search

